import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';

interface PostUpdateModalProps {
  projectId: string;
  projectDbId: string | null;
  onClose: () => void;
  onSuccess: (newUpdate: LocalUpdate) => void;
}

export interface LocalUpdate {
  id: string;
  role: 'Management' | 'Subcontractor' | 'Client';
  type: string;
  title: string;
  body: string;
  stage: string;
  timestamp: string;
  visibility: string;
  photos: { id: string; url: string; caption: string }[];
  projectId: string;
}

export default function PostUpdateModal({ projectId, projectDbId, onClose, onSuccess }: PostUpdateModalProps) {
  const { t } = useTranslation();

  const UPDATE_TYPES: Array<{ value: string; label: string }> = [
    { value: 'progress', label: t('notifType.progress') },
    { value: 'issue', label: t('notifType.issue') },
    { value: 'inspection', label: t('notifType.inspection') },
    { value: 'completion', label: t('status.completed') },
    { value: 'delay', label: t('status.delayed') },
  ];

  const ROLES: Array<{ value: 'Management' | 'Subcontractor' | 'Client'; label: string; color: string; icon: string }> = [
    { value: 'Management', label: t('tasks.roleManagement'), color: '#E8621A', icon: 'ri-building-2-line' },
    { value: 'Subcontractor', label: t('tasks.roleSubcontractor'), color: '#059669', icon: 'ri-tools-line' },
    { value: 'Client', label: t('tasks.roleClient'), color: '#374151', icon: 'ri-user-line' },
  ];

  const VISIBILITY_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'client', label: t('postUpdate.allParties') },
    { value: 'internal', label: t('postUpdate.managementOnly') },
    { value: 'subcontractor', label: t('postUpdate.mgmtAndSub') },
  ];

  const [role, setRole] = useState<'Management' | 'Subcontractor' | 'Client'>('Management');
  const [updateType, setUpdateType] = useState('progress');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState('client');
  const [isClientHighlight, setIsClientHighlight] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedRole = ROLES.find((r) => r.value === role)!;

  const handleSubmit = async () => {
    if (!title.trim()) { setError(t('postUpdate.enterTitle')); return; }
    if (!body.trim()) { setError(t('postUpdate.enterDetails')); return; }
    setError(null);
    setSaving(true);

    if (projectDbId) {
      const { data: userData } = await supabase.auth.getUser();
      const { error: dbError } = await supabase
        .from('jphph_progress_updates')
        .insert({
          project_id: projectDbId,
          update_title: title.trim(),
          body: body.trim(),
          update_type: updateType,
          visibility_scope: visibility,
          is_client_highlight: isClientHighlight,
          author_user_id: userData.user?.id ?? '00000000-0000-0000-0000-000000000000',
        });

      if (dbError) {
        setError(`${t('error.saveFailed')}: ${dbError.message}`);
        setSaving(false);
        return;
      }

      if (isClientHighlight || visibility === 'client') {
        await createNotification({
          project_id: projectDbId,
          notification_type: 'update',
          title: title.trim(),
          body: body.trim(),
          target_role: 'client',
          link_path: `/client/updates`,
          created_by: userData.user?.id ?? null,
        });
      }

      if (visibility === 'subcontractor' || visibility === 'client') {
        await createNotification({
          project_id: projectDbId,
          notification_type: 'update',
          title: title.trim(),
          body: body.trim(),
          target_role: 'subcontractor',
          link_path: `/subcontractor/project/${projectId}`,
          created_by: userData.user?.id ?? null,
        });
      }
    }

    const newUpdate: LocalUpdate = {
      id: `local-${Date.now()}`,
      role,
      type: updateType,
      title: title.trim(),
      body: body.trim(),
      stage: t('postUpdate.currentStage'),
      timestamp: t('postUpdate.justNow'),
      visibility,
      photos: [],
      projectId,
    };

    onSuccess(newUpdate);
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>

      <div className="relative w-full max-w-lg bg-white rounded-t-3xl lg:rounded-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-center pt-3 pb-1 lg:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-200"></div>
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-[#1A1A1A] font-bold text-base">{t('postUpdate.title')}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl text-[#9CA3AF] hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">

          {/* Role selector */}
          <div>
            <label className="text-[#374151] text-xs font-semibold uppercase tracking-wider mb-2 block">{t('postUpdate.postAs')}</label>
            <div className="grid grid-cols-3 gap-2">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setRole(r.value)}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                    role === r.value ? 'border-current' : 'border-gray-100 bg-gray-50'
                  }`}
                  style={role === r.value ? { borderColor: r.color, backgroundColor: `${r.color}10` } : {}}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: role === r.value ? `${r.color}20` : '#F3F4F6' }}
                  >
                    <i className={`${r.icon} text-sm`} style={{ color: role === r.value ? r.color : '#9CA3AF' }}></i>
                  </div>
                  <span
                    className="text-[11px] font-semibold"
                    style={{ color: role === r.value ? r.color : '#9CA3AF' }}
                  >
                    {r.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Update type */}
          <div>
            <label className="text-[#374151] text-xs font-semibold uppercase tracking-wider mb-2 block">{t('postUpdate.updateType')}</label>
            <div className="flex gap-2 flex-wrap">
              {UPDATE_TYPES.map((ut) => (
                <button
                  key={ut.value}
                  onClick={() => setUpdateType(ut.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                    updateType === ut.value
                      ? 'bg-[#E8621A] text-white border-[#E8621A]'
                      : 'bg-white text-[#6B7280] border-gray-200'
                  }`}
                >
                  {ut.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-[#374151] text-xs font-semibold uppercase tracking-wider mb-2 block">{t('form.title')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('postUpdate.titlePlaceholder')}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#9CA3AF] focus:outline-none focus:border-[#E8621A] transition-colors"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-[#374151] text-xs font-semibold uppercase tracking-wider mb-2 block">{t('postUpdate.details')}</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 500))}
              placeholder={t('postUpdate.detailsPlaceholder')}
              rows={4}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#9CA3AF] focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
            />
            <div className="text-right text-[10px] text-[#9CA3AF] mt-1">{body.length}/500</div>
          </div>

          {/* Visibility */}
          <div>
            <label className="text-[#374151] text-xs font-semibold uppercase tracking-wider mb-2 block">{t('dailyLog.visibility')}</label>
            <div className="space-y-2">
              {VISIBILITY_OPTIONS.map((v) => (
                <button
                  key={v.value}
                  onClick={() => setVisibility(v.value)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-pointer text-left ${
                    visibility === v.value ? 'border-[#E8621A] bg-[#FEF0E7]/50' : 'border-gray-100 bg-white'
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      visibility === v.value ? 'border-[#E8621A]' : 'border-gray-300'
                    }`}
                  >
                    {visibility === v.value && <div className="w-2 h-2 rounded-full bg-[#E8621A]"></div>}
                  </div>
                  <span className={`text-sm font-medium ${visibility === v.value ? 'text-[#E8621A]' : 'text-[#374151]'}`}>{v.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Client highlight toggle */}
          <div className="flex items-center justify-between py-3 border-t border-gray-100">
            <div>
              <div className="text-[#374151] text-sm font-semibold">{t('postUpdate.clientHighlight')}</div>
              <div className="text-[#9CA3AF] text-xs mt-0.5">{t('postUpdate.clientHighlightDesc')}</div>
            </div>
            <button
              onClick={() => setIsClientHighlight((v) => !v)}
              className={`w-12 h-6 rounded-full transition-colors cursor-pointer relative ${isClientHighlight ? 'bg-[#E8621A]' : 'bg-gray-200'}`}
            >
              <div
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${isClientHighlight ? 'translate-x-6' : 'translate-x-0.5'}`}
              ></div>
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-3 text-[#EF4444] text-sm">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full py-3.5 rounded-xl font-semibold text-sm text-white cursor-pointer transition-colors disabled:opacity-60 whitespace-nowrap"
            style={{ backgroundColor: selectedRole.color }}
          >
            {saving ? t('postUpdate.posting') : t('postUpdate.postAs', { role: selectedRole.label })}
          </button>
        </div>
      </div>
    </div>
  );
}